import { useTranslation } from 'react-i18next';
import {
  Button,
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemGroup,
  ItemMedia,
  ItemTitle,
  Skeleton
} from '@bliks-io/ui';
import type { DuplicateCandidate, EntitySuggestionKind } from '@bliks-io/contracts';
import { ENTITY_ICONS } from '@/constants/entityIcons';
import { SystemSelect } from '@/components/form/SystemSelect';
import { OrgFunctionSelect } from '@/components/form/OrgFunctionSelect';
import { useSystems } from '@/api/queries/useSystems';
import { useOrgFunctions } from '@/api/queries/useOrgFunctions';

export interface MergeTarget {
  id: string;
  name: string;
}

interface ExistingEntityMergePanelProps {
  kind: EntitySuggestionKind;
  candidates: DuplicateCandidate[];
  isLoadingCandidates: boolean;
  /** Currently picked value of the manual lookup select; pass undefined when no selection should be visible. */
  selectValue: string | undefined;
  /** Called whenever the user picks an existing entity to merge into — either via a candidate row or the manual selector. */
  onPickTarget: (target: MergeTarget) => void;
  /** Disable all interactions (used while a confirmation dialog is open or while merging). */
  disabled: boolean;
}

/**
 * Combined "merge with an existing entity" section. Surfaces auto-detected
 * duplicate candidates (when present) and a manual searchable selector for any
 * other existing entity, sharing one bordered container so the admin treats
 * "merge into an existing entry" as a single decision space distinct from
 * "create a new entry". Picking a target opens a confirmation dialog at the
 * caller; this panel never triggers the merge mutation itself.
 */
export function ExistingEntityMergePanel({
  kind,
  candidates,
  isLoadingCandidates,
  selectValue,
  onPickTarget,
  disabled
}: ExistingEntityMergePanelProps) {
  const { t } = useTranslation();
  const entityCandidates = candidates.filter((c) => c.source === 'entity');
  const Icon = ENTITY_ICONS[kind];

  return (
    <div className="bg-muted/30 flex flex-col gap-3 rounded-md border p-4">
      <p className="text-foreground text-sm font-medium">
        {t(`entitySuggestion.review.mergeWithExistingSection.${kind}`)}
      </p>
      {isLoadingCandidates && (
        <Skeleton role="status" aria-label={t('common.loading')} className="h-12" />
      )}
      {entityCandidates.length > 0 && (
        <ItemGroup>
          {entityCandidates.map((c) => {
            const descriptionParts = [
              c.preview.secondaryLine,
              ...c.reasons.map((r) => t(`entitySuggestion.duplicates.reasons.${r}`))
            ].filter(Boolean);
            return (
              <Item key={`entity:${c.id}`} variant="default" size="sm">
                <ItemMedia variant="icon">
                  <Icon />
                </ItemMedia>
                <ItemContent>
                  <ItemTitle>{c.preview.name}</ItemTitle>
                  {descriptionParts.length > 0 && (
                    <ItemDescription>{descriptionParts.join(' · ')}</ItemDescription>
                  )}
                </ItemContent>
                <ItemActions>
                  <Button
                    variant="link"
                    size="small"
                    type="button"
                    disabled={disabled}
                    onClick={() => onPickTarget({ id: c.id, name: c.preview.name })}
                  >
                    {t(`entitySuggestion.duplicates.useThisInstead.${kind}`)}
                  </Button>
                </ItemActions>
              </Item>
            );
          })}
        </ItemGroup>
      )}
      {kind === 'system' ? (
        <SystemPickerControl
          selectValue={selectValue}
          onPickTarget={onPickTarget}
          disabled={disabled}
        />
      ) : (
        <OrgFunctionPickerControl
          selectValue={selectValue}
          onPickTarget={onPickTarget}
          disabled={disabled}
        />
      )}
    </div>
  );
}

function SystemPickerControl({
  selectValue,
  onPickTarget,
  disabled
}: {
  selectValue: string | undefined;
  onPickTarget: (target: MergeTarget) => void;
  disabled: boolean;
}) {
  const { t } = useTranslation();
  const { data: systems = [] } = useSystems();
  return (
    <SystemSelect
      value={selectValue}
      onChange={(value) => {
        if (typeof value !== 'string') return;
        const found = systems.find((s) => s._id === value);
        onPickTarget({ id: value, name: found?.name ?? value });
      }}
      placeholder={t('entitySuggestion.review.mergeSelectPlaceholder.system')}
      disabled={disabled}
    />
  );
}

function OrgFunctionPickerControl({
  selectValue,
  onPickTarget,
  disabled
}: {
  selectValue: string | undefined;
  onPickTarget: (target: MergeTarget) => void;
  disabled: boolean;
}) {
  const { t } = useTranslation();
  const { data: orgFunctions = [] } = useOrgFunctions();
  return (
    <OrgFunctionSelect
      value={selectValue}
      onChange={(value) => {
        if (typeof value !== 'string') return;
        const found = orgFunctions.find((orgFunction) => orgFunction._id === value);
        onPickTarget({ id: value, name: found?.name ?? value });
      }}
      placeholder={t('entitySuggestion.review.mergeSelectPlaceholder.orgFunction')}
      disabled={disabled}
    />
  );
}
